import { useState, useEffect, useCallback } from 'react';

export const useAppEditor = (supabase, appId) => {
  const isEditing = !!appId;

  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon_url: '',
    logo_url: '',
    theme_color: '#2563eb',
    background_color: '#ffffff',
    branding_text: ''
  });

  // State for linked scenarios
  const [linkedScenarios, setLinkedScenarios] = useState([]);
  const [allScenarios, setAllScenarios] = useState([]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch app data from Supabase
  const fetchApp = useCallback(async () => {
    if (!isEditing) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('apps')
        .select('*')
        .eq('id', appId)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          name: data.name || '',
          description: data.description || '',
          token: data.token || '',
          icon_url: data.icon_url || '',
          logo_url: data.logo_url || '',
          theme_color: data.theme_color || '#2563eb',
          background_color: data.background_color || '#ffffff',
          branding_text: data.branding_text || ''
        });
      }
    } catch (error) {
      console.error('Error fetching app:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [supabase, appId, isEditing]);

  // Fetch linked scenarios
  const fetchLinkedScenarios = useCallback(async () => {
    if (!isEditing) return;

    try {
      const { data, error } = await supabase
        .from('app_scenarios')
        .select(`
          id,
          display_order,
          scenarios (
            id,
            token,
            name,
            description
          )
        `)
        .eq('app_id', appId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Format the data
      const formatted = (data || []).map(item => ({
        linkId: item.id,
        display_order: item.display_order,
        id: item.scenarios.id,
        token: item.scenarios.token,
        name: item.scenarios.name,
        description: item.scenarios.description
      }));

      setLinkedScenarios(formatted);
    } catch (error) {
      console.error('Error fetching linked scenarios:', error);
      setError(error.message);
    }
  }, [supabase, appId, isEditing]);

  // Fetch all available scenarios
  const fetchAllScenarios = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return;

      const { data, error } = await supabase
        .from('scenarios')
        .select('id, token, name, description')
        .eq('user_id', user.id)
        .order('name', { ascending: true });

      if (error) throw error;

      setAllScenarios(data || []);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      setError(error.message);
    }
  }, [supabase]);

  // Load data on mount
  useEffect(() => {
    if (isEditing) {
      fetchApp();
      fetchLinkedScenarios();
    }
    fetchAllScenarios();
  }, [isEditing, fetchApp, fetchLinkedScenarios, fetchAllScenarios]);

  // Update form field
  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save app (create or update)
  const saveApp = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate required fields
      if (!formData.name) {
        throw new Error('App name is required');
      }

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const appData = {
        name: formData.name,
        description: formData.description,
        icon_url: formData.icon_url,
        logo_url: formData.logo_url,
        theme_color: formData.theme_color,
        background_color: formData.background_color,
        branding_text: formData.branding_text,
        user_id: user.id
      };

      let savedApp;

      if (isEditing) {
        // Update existing app
        const { data, error } = await supabase
          .from('apps')
          .update({ ...appData, updated_at: new Date().toISOString() })
          .eq('id', appId)
          .select();

        if (error) throw error;
        savedApp = data[0];
      } else {
        // Create new app
        const { data, error } = await supabase
          .from('apps')
          .insert([appData])
          .select();

        if (error) throw error;
        savedApp = data[0];
      }

      return savedApp;
    } catch (error) {
      console.error('Error saving app:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Add scenario to app
  const addScenario = async (scenarioId, displayOrder = null) => {
    try {
      if (!isEditing) {
        throw new Error('Cannot add scenarios before app is created');
      }

      // If no display order specified, use next available
      if (displayOrder === null) {
        displayOrder = linkedScenarios.length;
      }

      const { data, error } = await supabase
        .from('app_scenarios')
        .insert([{
          app_id: appId,
          scenario_id: scenarioId,
          display_order: displayOrder
        }])
        .select();

      if (error) throw error;

      // Refresh linked scenarios
      await fetchLinkedScenarios();

      return data[0];
    } catch (error) {
      console.error('Error adding scenario:', error);
      setError(error.message);
      throw error;
    }
  };

  // Remove scenario from app
  const removeScenario = async (linkId) => {
    try {
      const { error } = await supabase
        .from('app_scenarios')
        .delete()
        .eq('id', linkId);

      if (error) throw error;

      // Update local state
      setLinkedScenarios(prev => prev.filter(s => s.linkId !== linkId));
    } catch (error) {
      console.error('Error removing scenario:', error);
      setError(error.message);
      throw error;
    }
  };

  // Reorder scenarios
  const reorderScenarios = async (scenarios) => {
    try {
      // Update display_order for all scenarios
      const updates = scenarios.map((scenario, index) => ({
        id: scenario.linkId,
        display_order: index
      }));

      // Update each scenario's display order
      await Promise.all(
        updates.map(update =>
          supabase
            .from('app_scenarios')
            .update({ display_order: update.display_order })
            .eq('id', update.id)
        )
      );

      // Update local state
      setLinkedScenarios(scenarios.map((s, index) => ({
        ...s,
        display_order: index
      })));
    } catch (error) {
      console.error('Error reordering scenarios:', error);
      setError(error.message);
      throw error;
    }
  };

  // Get available scenarios (not yet linked)
  const getAvailableScenarios = () => {
    const linkedIds = linkedScenarios.map(s => s.id);
    return allScenarios.filter(s => !linkedIds.includes(s.id));
  };

  return {
    formData,
    linkedScenarios,
    allScenarios,
    isLoading,
    isSaving,
    error,
    isEditing,
    updateFormField,
    saveApp,
    addScenario,
    removeScenario,
    reorderScenarios,
    getAvailableScenarios,
    refreshApp: fetchApp,
    refreshLinkedScenarios: fetchLinkedScenarios
  };
};
